import { useCallback, useEffect, useState } from 'react'
import { FormField } from '../api'

interface StepProps {
  id: 'planning_basics' | 'setup_details' | 'completion'
  name: string
}

interface UseFormValidationProps {
  appStateContext: any
  steps: StepProps[]
  activeStep: number
  setActiveStep: (step: number) => void
}

export const useFormValidation = ({ appStateContext, steps, activeStep, setActiveStep }: UseFormValidationProps) => {
  const [shouldScrollToError, setShouldScrollToError] = useState(false)

  const findFirstError = useCallback(() => {
    const extendedData = appStateContext?.state.currentChat?.extended_data
    if (!extendedData?.experiment) return null

    // Create an array of steps to check, starting from the current step
    const stepsToCheck = [...steps.slice(activeStep), ...steps.slice(0, activeStep)]

    for (const step of stepsToCheck) {
      const data = extendedData.experiment[step.id]
      if (!data) continue

      for (const fieldName of Object.keys(data)) {
        const field = data[fieldName as keyof typeof data] as FormField<unknown>
        if ((field?.errors?.length ?? 0) > 0) {
          return { stepId: step.id, fieldName }
        }
      }
    }
    return null
  }, [appStateContext?.state.currentChat?.extended_data, activeStep])

  const scrollToError = useCallback(() => {
    const error = findFirstError()
    if (!error) return

    const stepIndex = steps.findIndex(s => s.id === error.stepId)
    if (stepIndex !== activeStep) {
      setActiveStep(stepIndex)
    }

    setTimeout(() => {
      const element = document.getElementById(`field_${error.fieldName}`)
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
        })
      }
      setShouldScrollToError(false)
    }, 100)
  }, [findFirstError, activeStep, setActiveStep])

  useEffect(() => {
    if (shouldScrollToError) {
      scrollToError()
    }
  }, [shouldScrollToError, scrollToError])

  return { setShouldScrollToError }
}
